import { JourneyNode } from "@/components/ui/atoms/journey-node";
import { ProgramLocked } from "@/components/learn/program-locked";
import { MarkdownText } from "@/components/ui/atoms/markdown-text";
import { ExerciseRow } from "@/components/ui/molecules/exercise-row";
import type {
  ProgramDetail,
  ProgramExerciseSummary,
  ProgramMilestone,
} from "@/lib/api/api-client";
import type { MilestoneState } from "@/lib/learning-journey/program-state";
import { useProgramProgress } from "@/lib/learning-journey/use-program-progress";

export interface ProgramJourneyProps {
  program: ProgramDetail;
  /** Accent hex color */
  color: string;
  /** HSL hue, currently unused but kept for theming flexibility */
  hue: number;
  onBack: () => void;
  /**
   * `origin` dit ce que l'adresse de destination ne dit pas : un exercice
   * d'étape est au programme, un remède est tiré par le morceau qui le
   * réclame. Les deux ouvrent la même page.
   */
  onOpenExercise: (
    exercise: ProgramExerciseSummary,
    origin: "step" | "remedy",
  ) => void;
  onOpenPiece: (piece: {
    id: string;
    slug?: string | null;
    title?: string | null;
  }) => void;
}

export function ProgramJourney({
  program,
  color,
  onBack,
  onOpenExercise,
  onOpenPiece,
}: ProgramJourneyProps) {
  const progress = useProgramProgress(program);

  // A piece that left the library would render a milestone linking nowhere.
  const milestones = program.milestones.filter(
    (m) => m.kind !== "piece" || m.pieceId !== null,
  );

  const stateById = new Map(
    (progress?.milestones ?? []).map((m) => [m.milestone.id, m]),
  );

  return (
    <div className="mx-auto max-w-[700px] px-6 pb-20 pt-8">
      {/* Back button */}
      <button
        onClick={onBack}
        className="-ml-3 mb-8 flex items-center gap-2 rounded-md px-3 py-1.5 text-[13px] text-text-muted transition-colors hover:bg-white/[0.04] hover:text-text-primary"
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M10 3L5 8l5 5" />
        </svg>
        Tous les parcours
      </button>

      {/* ── Hero ── */}
      <header className="mb-12">
        <span
          className="mb-3 block text-[11px] font-semibold uppercase tracking-[0.15em]"
          style={{ color }}
        >
          Niveau {program.level}
        </span>
        <h1 className="mb-3 text-[32px] font-bold leading-tight tracking-tight text-text-primary">
          {program.title}
        </h1>
        <p className="mb-5 max-w-[60ch] text-[17px] font-medium leading-relaxed text-text-primary">
          {program.goal}
        </p>
        {program.description && (
          <div className="max-w-[60ch]">
            <MarkdownText>{program.description}</MarkdownText>
          </div>
        )}
      </header>

      <div className="mb-12 h-px bg-white/[0.06]" />

      {/*
        Le serveur n'envoie pas les jalons d'un parcours fermé : il n'y a donc
        rien à masquer ici, seulement autre chose à montrer.
      */}
      {program.locked && <ProgramLocked program={program} color={color} />}

      {/* ── Milestones ── */}
      <ol className="flex flex-col gap-12">
        {milestones.map((milestone, index) => (
          <li key={milestone.id} className="flex gap-6">
            <div className="shrink-0 pt-1">
              <JourneyNode index={index + 1} color={color} />
            </div>

            <div className="min-w-0 flex-1">
              <Milestone
                milestone={milestone}
                color={color}
                state={stateById.get(milestone.id)?.state ?? null}
                reached={stateById.get(milestone.id)?.reached ?? 0}
                onOpenExercise={onOpenExercise}
                onOpenPiece={onOpenPiece}
              />
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}

function Milestone({
  milestone,
  color,
  state,
  reached,
  onOpenExercise,
  onOpenPiece,
}: {
  milestone: ProgramMilestone;
  color: string;
  /** `null` tant que la progression locale n'a pas été lue. */
  state: MilestoneState | null;
  reached: number;
  onOpenExercise: (
    exercise: ProgramExerciseSummary,
    origin: "step" | "remedy",
  ) => void;
  onOpenPiece: (piece: {
    id: string;
    slug?: string | null;
    title?: string | null;
  }) => void;
}) {
  if (milestone.kind === "step") {
    return (
      <>
        <h3 className="mb-1.5 text-lg font-semibold leading-snug text-text-primary">
          {milestone.title}
        </h3>
        <p className="mb-4 max-w-[55ch] text-[13px] leading-relaxed text-text-muted">
          {milestone.goal}
        </p>

        <div className="-mx-3 flex flex-col gap-0.5">
          {milestone.exercises.map((exercise) => (
            <ExerciseRow
              key={exercise.id}
              title={exercise.title}
              category={exercise.category ?? "fondations"}
              onClick={() => onOpenExercise(exercise, "step")}
            />
          ))}
        </div>
      </>
    );
  }

  return (
    <>
      <div className="mb-1.5 flex items-baseline gap-2.5">
        <h3 className="text-lg font-semibold leading-snug text-text-primary">
          {milestone.pieceTitle ?? milestone.pieceKey}
        </h3>
        {milestone.reward && (
          <span
            className="shrink-0 text-[10px] font-semibold uppercase tracking-[0.12em]"
            style={{ color }}
          >
            Récompense
          </span>
        )}
        {state === "done" && (
          <span className="shrink-0 text-[10px] font-semibold uppercase tracking-[0.12em] text-ok">
            Joué
          </span>
        )}
      </div>

      {/*
        On dit la part franchie plutôt qu'un simple « pas encore » : un morceau
        n'est pas un objet qui bascule, c'est une suite d'unités de travail.
        « 9 unités sur 12 » est une progression, « pas encore » est un mur.
      */}
      {state === "current" && reached > 0 && (
        <p className="mb-2 text-[12px] text-text-soft">
          {Math.round(reached * 100)} % des unités sont au niveau demandé
        </p>
      )}

      <p className="mb-4 max-w-[55ch] text-[13px] leading-relaxed text-text-secondary">
        {milestone.intent}
      </p>

      <button
        onClick={() =>
          milestone.pieceId &&
          onOpenPiece({
            id: milestone.pieceId,
            slug: milestone.pieceSlug,
            title: milestone.pieceTitle,
          })
        }
        className="mb-5 rounded-lg px-3.5 py-2 text-[13px] font-semibold text-bg-deep transition-opacity hover:opacity-90"
        style={{ backgroundColor: color }}
      >
        {state === "done" ? "Rejouer ce morceau" : "Jouer ce morceau"}
      </button>

      {milestone.remedies.length > 0 && (
        <div className="flex flex-col gap-2.5">
          <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-text-muted">
            Ce que ce morceau demande
          </span>

          {milestone.remedies.map((remedy) => (
            <div key={remedy.vefId} className="flex flex-col gap-1">
              <p className="max-w-[55ch] text-[12.5px] leading-relaxed text-text-soft">
                {remedy.because}
              </p>
              {remedy.exercise && (
                <div className="-mx-3">
                  <ExerciseRow
                    title={remedy.exercise.title}
                    category={remedy.exercise.category ?? "fondations"}
                    onClick={() => onOpenExercise(remedy.exercise!, "remedy")}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </>
  );
}
